// Service de notifications pour l'application Allure
export interface NotificationData {
  id: string;
  type: 'request_sent' | 'request_accepted' | 'request_rejected' | 'return_reminder' | 'favorite_rented' | 'payment_success' | 'damage_report';
  title: string;
  message: string;
  userId: string;
  dressId?: string;
  bookingId?: string;
  isRead: boolean;
  createdAt: Date;
  scheduledFor?: Date; // Pour les rappels programmés
  metadata?: {
    startDate?: string;
    endDate?: string;
    dressTitle?: string;
    ownerName?: string;
    renterName?: string;
    amount?: number;
  };
}

export interface NotificationSettings {
  emailNotifications: boolean;
  smsNotifications: boolean;
  pushNotifications: boolean;
  reminderDays: number; // Nombre de jours avant le rappel de retour
}

class NotificationService {
  private notifications: NotificationData[] = [];
  private settings: NotificationSettings = {
    emailNotifications: true,
    smsNotifications: true,
    pushNotifications: true,
    reminderDays: 1
  };

  // Simuler l'envoi d'email (en production, utiliser un service comme SendGrid, Mailgun, etc.)
  private async sendEmail(to: string, subject: string, body: string): Promise<boolean> {
    console.log(`📧 Email envoyé à ${to}:`, { subject, body });
    // Simulation d'un délai d'envoi
    await new Promise(resolve => setTimeout(resolve, 1000));
    return true;
  }

  // Simuler l'envoi de SMS (en production, utiliser un service comme Twilio, etc.)
  private async sendSMS(to: string, message: string): Promise<boolean> {
    console.log(`📱 SMS envoyé à ${to}:`, message);
    // Simulation d'un délai d'envoi
    await new Promise(resolve => setTimeout(resolve, 500));
    return true;
  }

  // Créer une nouvelle notification
  async createNotification(notification: Omit<NotificationData, 'id' | 'createdAt' | 'isRead'>): Promise<NotificationData> {
    const newNotification: NotificationData = {
      ...notification,
      id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date(),
      isRead: false
    };

    this.notifications.push(newNotification);
    
    // Sauvegarder dans localStorage pour la démo
    localStorage.setItem('allure_notifications', JSON.stringify(this.notifications));

    // Envoyer les notifications selon les préférences
    await this.sendNotification(newNotification);

    return newNotification;
  }

  // Envoyer une notification selon les préférences utilisateur
  private async sendNotification(notification: NotificationData): Promise<void> {
    // En production, récupérer les préférences utilisateur depuis la base de données
    const userSettings = this.getUserSettings(notification.userId);

    try {
      if (userSettings.emailNotifications) {
        await this.sendEmail(
          `${notification.userId}@example.com`, // En production, récupérer l'email réel
          notification.title,
          notification.message
        );
      }

      if (userSettings.smsNotifications) {
        await this.sendSMS(
          '+33123456789', // En production, récupérer le numéro réel
          `${notification.title}: ${notification.message}`
        );
      }

      if (userSettings.pushNotifications) {
        // En production, utiliser un service comme Firebase Cloud Messaging
        console.log('🔔 Push notification:', notification.title);
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi des notifications:', error);
    }
  }

  // Obtenir les notifications d'un utilisateur
  async getUserNotifications(userId: string): Promise<NotificationData[]> {
    // En production, récupérer depuis la base de données
    const stored = localStorage.getItem('allure_notifications');
    if (stored) {
      this.notifications = JSON.parse(stored);
    }
    
    return this.notifications.filter(notif => notif.userId === userId);
  }

  // Marquer une notification comme lue
  async markAsRead(notificationId: string): Promise<void> {
    const notification = this.notifications.find(n => n.id === notificationId);
    if (notification) {
      notification.isRead = true;
      localStorage.setItem('allure_notifications', JSON.stringify(this.notifications));
    }
  }

  // Marquer toutes les notifications comme lues
  async markAllAsRead(userId: string): Promise<void> {
    this.notifications.forEach(notif => {
      if (notif.userId === userId) {
        notif.isRead = true;
      }
    });
    localStorage.setItem('allure_notifications', JSON.stringify(this.notifications));
  }

  // Obtenir les paramètres de notification d'un utilisateur
  private getUserSettings(userId: string): NotificationSettings {
    // En production, récupérer depuis la base de données
    return this.settings;
  }

  // Programmer un rappel de retour
  async scheduleReturnReminder(bookingId: string, userId: string, endDate: string, dressTitle: string): Promise<void> {
    const endDateTime = new Date(endDate);
    const reminderDate = new Date(endDateTime.getTime() - (this.settings.reminderDays * 24 * 60 * 60 * 1000));
    
    if (reminderDate > new Date()) {
      await this.createNotification({
        type: 'return_reminder',
        title: '🔄 Rappel de retour',
        message: `N'oubliez pas de retourner la robe "${dressTitle}" le ${endDate}`,
        userId,
        bookingId,
        scheduledFor: reminderDate,
        metadata: {
          endDate,
          dressTitle
        }
      });
    }
  }

  // Notifier qu'une robe favorite a été louée
  async notifyFavoriteRented(userId: string, dressTitle: string, startDate: string, endDate: string, ownerName: string): Promise<void> {
    await this.createNotification({
      type: 'favorite_rented',
      title: '💔 Robe favorite louée',
      message: `La robe "${dressTitle}" que vous aimez a été louée du ${startDate} au ${endDate} par ${ownerName}`,
      userId,
      metadata: {
        dressTitle,
        startDate,
        endDate,
        ownerName
      }
    });
  }

  // Notifier l'acceptation d'une demande
  async notifyRequestAccepted(userId: string, dressTitle: string, startDate: string, endDate: string): Promise<void> {
    await this.createNotification({
      type: 'request_accepted',
      title: '✅ Demande acceptée',
      message: `Votre demande pour la robe "${dressTitle}" du ${startDate} au ${endDate} a été acceptée !`,
      userId,
      metadata: {
        dressTitle,
        startDate,
        endDate
      }
    });
  }

  // Notifier le refus d'une demande
  async notifyRequestRejected(userId: string, dressTitle: string, startDate: string, endDate: string, reason?: string): Promise<void> {
    await this.createNotification({
      type: 'request_rejected',
      title: '❌ Demande refusée',
      message: `Votre demande pour la robe "${dressTitle}" du ${startDate} au ${endDate} a été refusée.${reason ? ` Raison: ${reason}` : ''}`,
      userId,
      metadata: {
        dressTitle,
        startDate,
        endDate
      }
    });
  }

  // Notifier l'envoi d'une demande
  async notifyRequestSent(ownerId: string, renterName: string, dressTitle: string, startDate: string, endDate: string): Promise<void> {
    await this.createNotification({
      type: 'request_sent',
      title: '📨 Nouvelle demande',
      message: `${renterName} souhaite louer votre robe "${dressTitle}" du ${startDate} au ${endDate}`,
      userId: ownerId,
      metadata: {
        dressTitle,
        startDate,
        endDate,
        renterName
      }
    });
  }

  // Notifier un paiement réussi
  async notifyPaymentSuccess(userId: string, dressTitle: string, amount: number, bookingId: string): Promise<void> {
    await this.createNotification({
      type: 'payment_success',
      title: '💳 Paiement confirmé',
      message: `Votre paiement de ${amount}€ pour la robe "${dressTitle}" a été confirmé. Réservation #${bookingId}`,
      userId,
      bookingId,
      metadata: {
        dressTitle,
        amount
      }
    });
  }

  // Notifier un signalement de dommage
  async notifyDamageReport(ownerId: string, dressTitle: string, amount: number, description: string): Promise<void> {
    await this.createNotification({
      type: 'damage_report',
      title: '⚠️ Signalement de dommage',
      message: `Un dommage a été signalé sur votre robe "${dressTitle}". Montant estimé: ${amount}€. Description: ${description}`,
      userId: ownerId,
      metadata: {
        dressTitle,
        amount
      }
    });
  }

  // Obtenir le nombre de notifications non lues
  async getUnreadCount(userId: string): Promise<number> {
    const notifications = await this.getUserNotifications(userId);
    return notifications.filter(n => !n.isRead).length;
  }

  // Vérifier les rappels programmés (à appeler périodiquement)
  async checkScheduledReminders(): Promise<void> {
    const now = new Date();
    const pendingReminders = this.notifications.filter(
      n => n.scheduledFor && n.scheduledFor <= now && !n.isRead
    );

    for (const reminder of pendingReminders) {
      await this.sendNotification(reminder);
    }
  }

  // Mettre à jour les paramètres de notification
  async updateNotificationSettings(userId: string, settings: Partial<NotificationSettings>): Promise<void> {
    this.settings = { ...this.settings, ...settings };
    // En production, sauvegarder dans la base de données
    localStorage.setItem(`allure_notification_settings_${userId}`, JSON.stringify(this.settings));
  }

  // Obtenir les paramètres de notification
  async getNotificationSettings(userId: string): Promise<NotificationSettings> {
    const stored = localStorage.getItem(`allure_notification_settings_${userId}`);
    if (stored) {
      return JSON.parse(stored);
    }
    return this.settings;
  }
}

export const notificationService = new NotificationService();
